// Patch B.1 — Reusable Trial Lead picker for admin surfaces.
// Used inside the Booking Details modal to view/link/change/unlink the
// Trial Lead attached to an existing booking. Backend endpoint:
//   POST /api/admin/bookings/{booking_id}/link-trial-lead
//   Body: { trial_lead_id: string }  ("" = unlink)
//
// Props:
//   bookingId     — the booking whose linkage this widget manages
//   linkedLeadId  — current `booking.trial_lead_id` (string, may be "")
//   onLinkageChanged(newLeadId) — parent callback so it can refresh state
//   onOpenLead(leadId)          — parent callback to navigate to the lead
//
// The component does its OWN fetch of the current lead (when linkedLeadId
// is non-empty) plus the search list — no extra parent plumbing required.

import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Link as LinkIcon, Search, Loader2, Check, ExternalLink, X, RefreshCw,
} from "lucide-react";
import { Input } from "../ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("adminToken")}` });

export function TrialLeadPicker({ bookingId, linkedLeadId, onLinkageChanged, onOpenLead }) {
  const [current, setCurrent] = useState(null);   // resolved lead object, or null
  const [loadingCurrent, setLoadingCurrent] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [allLeadsCache, setAllLeadsCache] = useState(null); // memoized full list

  // Load the full leads list once (used for both current lookup and search filter).
  const loadAllLeads = async () => {
    if (allLeadsCache) return allLeadsCache;
    const r = await axios.get(`${API}/booking/admin/trial-leads`, { headers: auth() });
    const list = r.data?.leads || [];
    setAllLeadsCache(list);
    return list;
  };

  // Resolve the currently linked lead whenever the id changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!linkedLeadId) { setCurrent(null); return; }
      setLoadingCurrent(true);
      try {
        const list = await loadAllLeads();
        if (cancelled) return;
        const found = list.find((l) => l.id === linkedLeadId) || null;
        setCurrent(found);
      } catch {
        setCurrent(null);
      } finally {
        if (!cancelled) setLoadingCurrent(false);
      }
    })();
    return () => { cancelled = true; };
  }, [linkedLeadId]);

  // Debounced search filter over the memoized list.
  useEffect(() => {
    if (!searchOpen) return;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const list = await loadAllLeads();
        const q = query.trim().toLowerCase();
        const filtered = q
          ? list.filter((l) =>
              (l.name || "").toLowerCase().includes(q) ||
              (l.email || "").toLowerCase().includes(q) ||
              (l.phone || "").toLowerCase().includes(q))
          : list.slice(0, 25);
        // Exclude the already-linked one from picker suggestions.
        setResults(filtered.filter((l) => l.id !== linkedLeadId).slice(0, 25));
      } catch (err) {
        if (err.response?.status === 401) toast.error("Session expired");
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, searchOpen, linkedLeadId]);

  const callLink = async (newLeadId) => {
    setSaving(true);
    try {
      const r = await axios.post(
        `${API}/admin/bookings/${bookingId}/link-trial-lead`,
        { trial_lead_id: newLeadId || "" },
        { headers: auth() },
      );
      if (!r.data?.ok) throw new Error("Server rejected the update");
      onLinkageChanged?.(r.data.trial_lead_id || "");
      setSearchOpen(false);
      setQuery("");
      toast.success(newLeadId ? "Trial lead linked" : "Trial lead unlinked");
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Failed to update linkage";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const cardCls = "border border-[#282828]/10 bg-[#FAF8F0]/40 p-3";
  const primaryBtnCls = "text-[10px] tracking-wider uppercase px-3 py-1.5 bg-[#3D2B1F] text-[#C4A77D] hover:opacity-90 inline-flex items-center gap-1.5 disabled:opacity-50";
  const ghostBtnCls = "text-[10px] tracking-wider uppercase px-3 py-1.5 border border-[#282828]/20 text-[#3D2B1F] hover:bg-[#282828]/5 inline-flex items-center gap-1.5 disabled:opacity-50";
  const dangerBtnCls = "text-[10px] tracking-wider uppercase px-3 py-1.5 border border-red-300 text-red-700 hover:bg-red-50 inline-flex items-center gap-1.5 disabled:opacity-50";

  const header = (
    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#676664] mb-2">
      <LinkIcon className="w-3 h-3" /> Linked Trial Lead
    </div>
  );

  // ─── STATE 1: linked, resolved ───
  if (linkedLeadId && current) {
    const trialWhen = [current.preferred_date, current.preferred_time].filter(Boolean).join(" · ");
    return (
      <div className={cardCls} data-testid="booking-trial-lead-section">
        {header}
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[13px] text-[#282828] font-medium truncate" data-testid="linked-lead-name">
              {current.name || "—"}
            </div>
            <div className="text-[11px] text-[#676664]/80 truncate">
              {[current.email, current.phone].filter(Boolean).join(" · ") || "—"}
            </div>
            {trialWhen && (
              <div className="text-[11px] text-[#676664]/70 mt-0.5">Trial: {trialWhen}</div>
            )}
            {current.source && (
              <div className="text-[10px] text-[#676664]/50 uppercase tracking-wider mt-0.5">
                Source: {current.source}{current.status ? ` · ${current.status}` : ""}
              </div>
            )}
          </div>
        </div>

        {searchOpen ? (
          <SearchPanel
            query={query} setQuery={setQuery}
            results={results} searching={searching}
            onPick={callLink} saving={saving}
            onCancel={() => { setSearchOpen(false); setQuery(""); }}
          />
        ) : (
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={() => onOpenLead?.(current.id)}
              className={ghostBtnCls}
              data-testid="open-linked-lead-btn"
            ><ExternalLink className="w-3 h-3" /> Open Trial Lead</button>
            <button
              onClick={() => { setSearchOpen(true); setQuery(""); }}
              className={ghostBtnCls}
              data-testid="change-linked-lead-btn"
              disabled={saving}
            ><RefreshCw className="w-3 h-3" /> Change</button>
            <button
              onClick={() => callLink("")}
              className={dangerBtnCls}
              data-testid="unlink-lead-btn"
              disabled={saving}
            >{saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />} Unlink</button>
          </div>
        )}
      </div>
    );
  }

  // ─── STATE 2: linked, still resolving ───
  if (linkedLeadId && loadingCurrent) {
    return (
      <div className={cardCls} data-testid="booking-trial-lead-section">
        {header}
        <div className="text-[11px] text-[#676664]/70 flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading lead {String(linkedLeadId).slice(0, 12)}…
        </div>
      </div>
    );
  }

  // ─── STATE 3: linked, but lead not found (stale id) ───
  if (linkedLeadId && !current && !loadingCurrent) {
    return (
      <div className={cardCls} data-testid="booking-trial-lead-section">
        {header}
        <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 mb-2">
          Linked lead <span className="font-mono">{linkedLeadId}</span> was not found. It may have been deleted.
        </div>
        {searchOpen ? (
          <SearchPanel
            query={query} setQuery={setQuery}
            results={results} searching={searching}
            onPick={callLink} saving={saving}
            onCancel={() => { setSearchOpen(false); setQuery(""); }}
          />
        ) : (
          <div className="flex gap-2">
            <button onClick={() => { setSearchOpen(true); setQuery(""); }} className={ghostBtnCls}
              data-testid="change-linked-lead-btn">
              <Search className="w-3 h-3" /> Pick Another Lead
            </button>
            <button onClick={() => callLink("")} className={dangerBtnCls}
              data-testid="unlink-lead-btn" disabled={saving}>
              <X className="w-3 h-3" /> Clear Broken Link
            </button>
          </div>
        )}
      </div>
    );
  }

  // ─── STATE 4: no linkage ───
  return (
    <div className={cardCls} data-testid="booking-trial-lead-section">
      {header}
      {!searchOpen ? (
        <div className="flex items-center justify-between gap-3">
          <div className="text-[11px] text-[#676664]/70">No Trial Lead linked.</div>
          <button
            onClick={() => { setSearchOpen(true); setQuery(""); }}
            className={primaryBtnCls}
            data-testid="link-trial-lead-btn"
          ><LinkIcon className="w-3 h-3" /> Link Trial Lead</button>
        </div>
      ) : (
        <SearchPanel
          query={query} setQuery={setQuery}
          results={results} searching={searching}
          onPick={callLink} saving={saving}
          onCancel={() => { setSearchOpen(false); setQuery(""); }}
        />
      )}
    </div>
  );
}

function SearchPanel({ query, setQuery, results, searching, onPick, saving, onCancel }) {
  return (
    <div className="mt-2" data-testid="trial-lead-picker-search-panel">
      <div className="relative mb-2">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#676664]/40" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search trial leads by name, email, or phone…"
          className="pl-9 text-[12px] h-9"
          autoFocus
          data-testid="picker-search-input"
        />
      </div>
      {searching && (
        <div className="text-[10px] text-[#676664]/50 py-1 flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin" /> Searching leads…
        </div>
      )}
      <div className="max-h-60 overflow-y-auto divide-y divide-[#282828]/5 border border-[#282828]/8 bg-white">
        {results.map((l) => (
          <button
            key={l.id}
            onClick={() => onPick(l.id)}
            disabled={saving}
            className="w-full text-left px-3 py-2 hover:bg-[#FAF8F0]/60 disabled:opacity-50 flex items-start gap-2"
            data-testid={`picker-result-${l.id}`}
          >
            <Check className="w-3 h-3 mt-1 text-[#9A7B4F] opacity-0 group-hover:opacity-100" />
            <div className="flex-1 min-w-0">
              <div className="text-[12px] text-[#282828] flex items-center gap-2">
                {l.name || "—"}
                {l.status && <span className="text-[9px] uppercase tracking-wider text-[#676664]/60 border border-[#676664]/20 px-1.5 py-0.5">{l.status}</span>}
              </div>
              <div className="text-[10px] text-[#676664]/50">
                {l.email}{l.phone ? ` · ${l.phone}` : ""}{l.preferred_date ? ` · trial ${l.preferred_date}` : ""}
              </div>
            </div>
          </button>
        ))}
        {!searching && results.length === 0 && (
          <div className="px-3 py-4 text-center text-[11px] text-[#676664]/50">
            No matching leads.
          </div>
        )}
      </div>
      <div className="mt-2 flex justify-end">
        <button
          onClick={onCancel}
          disabled={saving}
          className="text-[10px] uppercase tracking-wider text-[#676664] hover:text-[#282828]"
          data-testid="picker-cancel-btn"
        >Cancel</button>
      </div>
    </div>
  );
}

export default TrialLeadPicker;
